import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, CheckCircle2, Globe } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';
import TaxCalculator from './TaxCalculator';
import { GCC_PAGES, GCC_LIST } from '../data/gccPages';
import { GCC_PAGES_AR } from '../data/gccPagesAr';

const SITE = 'https://moneyfeed.io';

const T = {
  en: {
    keyFigures: 'The key figures (2026)',
    estimate: 'Estimate your net VAT',
    estimateSub: (place) => `VAT you charge on sales minus VAT you paid on expenses — for ${place}.`,
    ctaHead: (place) => `Keep ${place} VAT tax-ready — on WhatsApp`,
    ctaSub: 'Snap every tax invoice, and MoneyFeed reads the VAT amount, TRN and net total, then keeps a VAT-ready record you can export any time.',
    scanner: 'Try the VAT receipt scanner',
    faq: (place, kind) => `${place} ${kind === 'einvoicing' ? 'e-invoicing' : 'VAT'} — FAQ`,
    disclaimer: 'Figures are 2026 headline guidance verified against FTA / ZATCA sources — not tax advice. Check current rules or an accountant for your situation.',
    more: 'More Middle East VAT & e-invoicing guides',
    langLink: 'العربية',
  },
  ar: {
    keyFigures: 'أبرز الأرقام (2026)',
    estimate: 'احسب صافي ضريبتك',
    estimateSub: (place) => `الضريبة التي تفرضها على المبيعات ناقص الضريبة التي دفعتها على المصروفات — لـ${place}.`,
    ctaHead: (place) => `أبقِ ضريبة ${place} جاهزة — عبر واتساب`,
    ctaSub: 'صوّر كل فاتورة ضريبية، ويقرأ MoneyFeed قيمة الضريبة ورقم التسجيل والإجمالي الصافي، ويحتفظ بسجلّ جاهز للضريبة يمكنك تصديره في أي وقت.',
    scanner: 'جرّب ماسح فواتير الضريبة',
    faq: (place, kind) => `${place} — ${kind === 'einvoicing' ? 'الفوترة الإلكترونية' : 'ضريبة القيمة المضافة'} — الأسئلة الشائعة`,
    disclaimer: 'الأرقام إرشادية لعام 2026 ومُتحقَّق منها من مصادر الهيئة الاتحادية للضرائب / هيئة الزكاة والضريبة — وليست استشارة ضريبية.',
    more: 'مزيد من أدلة الضريبة والفوترة الإلكترونية في الشرق الأوسط',
    langLink: 'English',
  },
};

export default function GccTaxLanding({ slug, lang = 'en' }) {
  const isAr = lang === 'ar';
  const src = isAr ? GCC_PAGES_AR : GCC_PAGES;
  const data = src[slug];
  if (!data) return <Navigate to="/" replace />;
  const t = T[lang] || T.en;

  const enPath = `/${slug}`;
  const arPath = `/ar/${slug}`;
  const path = isAr ? arPath : enPath;
  const langPrefix = isAr ? '/ar' : '';
  const altPath = isAr ? enPath : arPath;

  const [showArNudge, setShowArNudge] = React.useState(false);
  React.useEffect(() => {
    if (!isAr && typeof navigator !== 'undefined'
      && (navigator.language || '').toLowerCase().startsWith('ar')
      && !localStorage.getItem('mf_ar_nudge_dismissed')) {
      setShowArNudge(true);
    }
  }, [isAr]);
  const alternates = [
    { hreflang: 'en', href: SITE + enPath },
    { hreflang: 'ar', href: SITE + arPath },
    { hreflang: 'x-default', href: SITE + enPath },
  ];

  const others = GCC_LIST.filter((p) => p.slug !== slug);
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: data.h1, url: `${SITE}${path}`, description: data.seoDescription, inLanguage: lang },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE + '/' },
      { '@type': 'ListItem', position: 2, name: data.h1, item: `${SITE}${path}` },
    ] },
  ];

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid={`gcc-tax-landing-${slug}${isAr ? '-ar' : ''}`}>
      <Seo title={data.seoTitle} description={data.seoDescription} path={path} jsonLd={jsonLd} alternates={alternates} lang={lang} dir={isAr ? 'rtl' : 'ltr'} />
      {showArNudge && (
        <div dir="rtl" className="bg-[#064E3B] text-white text-sm px-4 py-2.5 flex items-center justify-center gap-4" data-testid="ar-nudge-banner">
          <Link to={arPath} className="font-bold underline hover:text-[#84CC16]" data-testid="ar-nudge-link">اقرأ هذه الصفحة بالعربية ←</Link>
          <button onClick={() => { localStorage.setItem('mf_ar_nudge_dismissed', '1'); setShowArNudge(false); }} className="opacity-70 hover:opacity-100" aria-label="إغلاق" data-testid="ar-nudge-dismiss">✕</button>
        </div>
      )}
      <LandingHeader />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-14 pb-8 text-center">
        <div className="flex justify-center mb-4">
          <Link to={altPath} data-testid="gcc-lang-toggle" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16]">
            <Globe className="h-4 w-4" /> {t.langLink}
          </Link>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ backgroundColor: `${data.accent}1A` }}>
          <Building2 className="h-4 w-4" style={{ color: data.accent }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: data.accent }}>{data.flag} {data.badge}</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">{data.h1}</h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">{data.intro}</p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-6">
        <div className="bg-white rounded-3xl shadow-md p-6 sm:p-8 border border-gray-100">
          <h2 className="text-xl font-bold text-[#064E3B] mb-4">{t.keyFigures}</h2>
          <ul className="space-y-3">
            {data.points.map((p, i) => (
              <li key={i} className="flex gap-3 text-gray-700" data-testid={`gcc-point-${i}`}>
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: data.accent }} />
                <span className="text-sm sm:text-base">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {data.kind === 'vat' && (
        <section className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
          <div className="bg-white rounded-3xl shadow-md p-4 sm:p-6 border border-gray-100">
            <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-1">{t.estimate}</h2>
            <p className="text-sm text-gray-500 text-center mb-5">{t.estimateSub(data.place)}</p>
            <div dir="ltr"><TaxCalculator initialRegion={data.calcRegion} embedded /></div>
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-4 pt-4">
        <div className="bg-[#064E3B] rounded-3xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">{t.ctaHead(data.place)}</h2>
          <p className="text-[#A7F3D0] text-sm mb-5 max-w-lg mx-auto">{t.ctaSub}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={data.ctaHref}>
              <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="gcc-cta">
                {data.ctaLabel} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/vat-receipt-scanner">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base" data-testid="gcc-scanner">{t.scanner}</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-2xl font-bold text-[#064E3B] text-center mb-8">{t.faq(data.place, data.kind)}</h2>
        <div className="space-y-4">
          {data.faqs.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`gcc-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 text-center mt-6">{t.disclaimer}</p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <h2 className="text-lg font-bold text-[#064E3B] mb-4 text-center">{t.more}</h2>
        <div className="flex flex-wrap justify-center gap-2">
          {others.map((o) => (
            <Link key={o.slug} to={`${langPrefix}/${o.slug}`} className="px-4 py-2 bg-white rounded-full text-sm font-semibold text-[#064E3B] shadow-sm border border-gray-200 hover:border-[#84CC16]" data-testid={`gcc-link-${o.slug}`}>
              {isAr ? `${GCC_PAGES_AR[o.slug].flag} ${GCC_PAGES_AR[o.slug].h1}` : o.label}
            </Link>
          ))}
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/whatsapp-bookkeeping" className="hover:text-[#064E3B]">WhatsApp Bookkeeping</Link>
          <Link to="/vat-receipt-scanner" className="hover:text-[#064E3B]">VAT Receipt Scanner</Link>
          <Link to="/auth" className="hover:text-[#064E3B]">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
